import LangEnum from '../../../../lang/LangEnum';
import { ProductDetailsSize } from '../../../../services/modules/productDetails/productDetails.types';
import { CreateGridParams } from '../../../offline/stores/offlineOrders/types/offlineOrders.actions.types';
import { OrderProductGridItem } from '../../../orders/products/orderProducts.types';
import { AvailabilityEnum } from '../../ProductTypes';
import { OfflineAvailability, toOfflineAvailability } from '../../utils/availability.utils';
import { CombinationButtonStatusEnum, IGridStatus } from './useCombination';

export interface ButtonStatusGridItem {
  orderItemQuantity?: number;
  orderItemId?: string;
}

interface GetGridStatusParams {
  hasEmptyAvailability: boolean;
  grid: ButtonStatusGridItem[];
}

const getGridParams = (grid: ButtonStatusGridItem[]) => {
  let itemQuantity = 0;
  let hasItemInOrder = false;

  for (let index = 0; index < grid.length; index++) {
    const gridItem = grid[index];
    itemQuantity += gridItem.orderItemQuantity || 0;

    if (!hasItemInOrder) {
      hasItemInOrder = !!gridItem.orderItemId;
    }
  }

  return {
    itemQuantity,
    hasItemInOrder,
  };
};

export const getButtonStatus = ({
  hasEmptyAvailability,
  grid = [],
}: GetGridStatusParams): IGridStatus => {
  const { itemQuantity, hasItemInOrder } = getGridParams(grid);

  if (hasEmptyAvailability) {
    return {
      type: CombinationButtonStatusEnum.SET_WINDOW,
      itemQuantity,
    };
  }

  if (itemQuantity > 0) {
    return {
      type: CombinationButtonStatusEnum.WITH_PIECES,
      itemQuantity,
    };
  }

  if (hasItemInOrder) {
    return {
      type: CombinationButtonStatusEnum.ADDED,
      itemQuantity,
    };
  }

  return {
    type: CombinationButtonStatusEnum.ADD,
    itemQuantity,
  };
};

export const getFakeProductItem = (size: ProductDetailsSize): OrderProductGridItem => {
  return {
    available: false,
    productSizeId: size.id,
    productSizeName: size.name,
    productSizeUnique: !!size.unique,
    productSizeActive: false,
    productSizePosition: 0,
    orderWholeSalePrice: 0,
    canceledQuantity: 0,
    pendingQuantity: 0,
    settledQuantity: 0,
    deliveryWindowAvailable: false,
    availableForReadyForDeliveryWithoutStock: false,
    skuId: `skuId-${size.id}`,
    reservedQuantity: 0,
    skuKits: [],
  };
};

export const isItemInOrderStatus = (status: IGridStatus): boolean =>
  [CombinationButtonStatusEnum.WITH_PIECES, CombinationButtonStatusEnum.ADDED].includes(
    status.type,
  );

export const hasPendingAvailabilityStatus = (status: IGridStatus): boolean =>
  status.type === CombinationButtonStatusEnum.SET_WINDOW;

export const isItemShownAsInOrder = (status: IGridStatus): boolean =>
  isItemInOrderStatus(status) || hasPendingAvailabilityStatus(status);

interface GetAddOrderButtonTooltipMessageParams {
  status: IGridStatus;
  tooltipMessage?: LangEnum;
}

export const getAddOrderButtonTooltipMessage = ({
  status,
  tooltipMessage,
}: GetAddOrderButtonTooltipMessageParams): LangEnum | undefined => {
  if (tooltipMessage) {
    return tooltipMessage;
  }

  return hasPendingAvailabilityStatus(status)
    ? LangEnum.PRODUCT_DETAILS_PENDING_AVAILABILITY_TOOLTIP
    : undefined;
};

interface BuildOfflineGridItemParams {
  availability: AvailabilityEnum;
  skuId: string;
  productColorId: string;
  deliveryWindowId?: string | null;
}

type WindowedOfflineAvailability =
  | AvailabilityEnum.INFINITE_PRE_ORDER
  | AvailabilityEnum.FUTURE_STOCK;

const getIsWindowedAvailability = (
  availability: OfflineAvailability,
): availability is WindowedOfflineAvailability =>
  availability === AvailabilityEnum.INFINITE_PRE_ORDER ||
  availability === AvailabilityEnum.FUTURE_STOCK;

export const buildOfflineGridItem = ({
  availability,
  skuId,
  productColorId,
  deliveryWindowId,
}: BuildOfflineGridItemParams): CreateGridParams | null => {
  const commonParams = { quantity: 0, skuId, productColorId };
  const offlineAvailability = toOfflineAvailability(availability);

  if (!getIsWindowedAvailability(offlineAvailability)) {
    return { ...commonParams, availability: offlineAvailability };
  }

  if (!deliveryWindowId) {
    return null;
  }

  return { ...commonParams, availability: offlineAvailability, deliveryWindowId };
};
